import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "letting-engine:saved-properties";

function readSavedIds(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is number => typeof id === "number");
  } catch {
    return [];
  }
}

function writeSavedIds(ids: number[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export function useSavedProperties() {
  const [savedIds, setSavedIds] = useState<number[]>(readSavedIds);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSavedIds(readSavedIds());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isSaved = useCallback(
    (id: number) => savedIds.includes(id),
    [savedIds]
  );

  const save = useCallback((id: number) => {
    setSavedIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeSavedIds(next);
      return next;
    });
  }, []);

  const unsave = useCallback((id: number) => {
    setSavedIds((prev) => {
      const next = prev.filter((savedId) => savedId !== id);
      writeSavedIds(next);
      return next;
    });
  }, []);

  const toggleSave = useCallback(
    (id: number) => {
      if (isSaved(id)) {
        unsave(id);
      } else {
        save(id);
      }
    },
    [isSaved, save, unsave]
  );

  return { savedIds, isSaved, save, unsave, toggleSave };
}
